import { z } from "zod";

// Upper bound on how many entities a single sender email can be scoped to.
// Keeps an authenticated request from pushing an unbounded UUID list into the
// parameterized `IN` query / bulk insert; comfortably above any realistic
// workspace entity count.
export const MAX_SENDING_EMAIL_ENTITY_SCOPES = 200;

export const sendingEmailResponseSchema = z.object({
    id: z.string().uuid(),
    sendingDomainId: z.string().uuid(),
    workspaceId: z.string().uuid().nullable(),
    organizationId: z.string().uuid().nullable(),
    email: z.string(),
    displayName: z.string().nullable(),
    isDefault: z.boolean(),
    // Empty array means available to every entity in the owning
    // workspace/organization (unscoped, current default behavior).
    entityUuids: z.array(z.string().uuid()),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type SendingEmailResponse = z.infer<typeof sendingEmailResponseSchema>;

export const listSendingEmailsResponseSchema = z.object({
    emails: z.array(sendingEmailResponseSchema),
});

export type ListSendingEmailsResponse = z.infer<
    typeof listSendingEmailsResponseSchema
>;

export const createSendingEmailSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(320),
    displayName: z.string().trim().min(1).max(255).nullable().optional(),
    isDefault: z.boolean().optional(),
    // Omitted/empty means available to every entity in the owning
    // workspace/organization (unscoped, current default behavior).
    entityUuids: z
        .array(z.string().uuid())
        .max(MAX_SENDING_EMAIL_ENTITY_SCOPES)
        .optional(),
});

export type CreateSendingEmailRequest = z.infer<
    typeof createSendingEmailSchema
>;

export const updateSendingEmailSchema = z.object({
    displayName: z.string().trim().min(1).max(255).nullable().optional(),
    isDefault: z.boolean().optional(),
    // Undefined leaves entity scoping unchanged; an array (including empty)
    // replaces the full set of scoped entities.
    entityUuids: z
        .array(z.string().uuid())
        .max(MAX_SENDING_EMAIL_ENTITY_SCOPES)
        .optional(),
});

export type UpdateSendingEmailRequest = z.infer<
    typeof updateSendingEmailSchema
>;
